import type { TractorWithAxlesResponse } from '@contracts/tractor/TractorWithAxlesResponse';
import { AxleAssembler } from '@src/axles/domain/AxleAssembler';
import { VehicleAxlesList } from '@src/axles/domain/VehicleAxlesList';
import type { AxleInformationInputsSchema } from '@src/orders/schemas/AxleInformationInputsSchema';
import { TractorList } from '@src/tractor/domain/Tractor';
import { TractorClient } from '@src/tractor/services/TractorClient';
import type { TractorFormInputs } from '@src/tractor/TractorFormInputs';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type * as yup from 'yup';

interface Props {
  clientId: string;
  tractor: TractorWithAxlesResponse;
  onSuccess: (updatedTractor: TractorWithAxlesResponse) => void;
  onError: () => void;
}

export const useUpdateTractor = ({
  clientId,
  tractor,
  onSuccess,
  onError,
}: Props) => {
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation({
    mutationFn: (data: yup.InferType<typeof TractorFormInputs>) => {
      return TractorClient.update(clientId, tractor.id, {
        unitNumber: data.tractorUnitNumber,
        make: data.tractorMake,
        model: data.tractorModelNumber,
        fuelType: data.tractorFuelType,
        bodyStyle: data.tractorBodyStyle,
        type: data.tractorType,
        vin: data.tractorVIN,
        year: data.tractorYear,
        specialDesign: data.vehicleOfSpecialDesign,
        licensePlate: data.tractorLicensePlateNumber,
        licensePlateState: data.tractorLicensePlateState,
        licensePlateEffectiveDate: data.tractorLicenseEffectiveDate,
        licensePlateExpirationDate: data.tractorLicenseExpirationDate,
        licensePlateType: data.tractorLicensePlateType,
        licensePlateYear: data.tractorLicensePlateYear,
        licensePlateIsApportioned: data.tractorLicenseIsApportionedPlate,
        length: data.tractorLength,
        width: data.tractorWidth,
        height: data.tractorHeight,
        wheelBase: data.tractorWheelBase,
        groundHeight: data.tractorGroundHeight,
        unladenWeight: data.tractorUnladenWeight,
        actualWeight: data.tractorActualWeight,
        gvw: data.tractorGVW,
        outerBridgeLength: data.tractorOuterBridgeLength,
        axles: data.tractorAxles.axles?.map(
          (
            axle: yup.InferType<typeof AxleInformationInputsSchema>,
            index: number,
          ) => AxleAssembler.toResponse(axle, tractor.axles[index]?.id),
        ),
      });
    },
    onSuccess: async (updatedTractor) => {
      onSuccess(updatedTractor);
      queryClient.setQueryData(
        ['tractors.list', clientId],
        (prev: TractorList | undefined) => {
          if (!prev) {
            return new TractorList([updatedTractor]);
          }
          return new TractorList([
            ...prev.tractors.filter((t) => t.id !== updatedTractor.id),
            updatedTractor,
          ]);
        },
      );
      queryClient.setQueryData(
        ['axles.list', clientId],
        (prev: VehicleAxlesList | undefined) => {
          if (!prev) {
            return new VehicleAxlesList({
              trailersAxles: [],
              tractorsAxles: [
                { vehicleId: updatedTractor.id, axles: updatedTractor.axles },
              ],
            });
          }
          return new VehicleAxlesList({
            trailersAxles: prev.trailersAxles,
            tractorsAxles: [
              { vehicleId: updatedTractor.id, axles: updatedTractor.axles },
            ],
          });
        },
      );
      await queryClient.invalidateQueries({
        queryKey: ['tractors.list', clientId],
      });
      await queryClient.invalidateQueries({
        queryKey: ['tractors.get', clientId, updatedTractor.id],
      });
      await queryClient.invalidateQueries({
        queryKey: ['axles.list', clientId],
      });
    },
    onError,
  });

  return {
    updateTractor: mutate,
    isLoading: isPending,
  };
};
